"use client";

import React, { useState, useEffect } from "react";
import { Edit2, Trash2, Search, Plus, X } from "lucide-react";
import { MdKeyboardArrowLeft, MdKeyboardArrowRight } from "react-icons/md";
import { useRoles } from "../../context/RoleContext";
import { useAuth } from "../../context/AuthContext";

// Reusable input class so every field shares the same lavender focus style
const inputClass =
  "w-full border border-gray-300 rounded-lg px-3 py-2 mb-4 outline-none transition-colors duration-150 focus:ring-2 focus:ring-[#8c57ff]/40 focus:border-[#8c57ff]/40";

export default function Role() {
  const {
    roles,
    createRole,
    updateRole,
    updateStatus,
    updatePermissions, // sidebar permissions
    updateActionPermissions, // optional (if you added), we'll prefer direct API if not present
    deleteRole,
    fetchRoles,
    getPermissionsByRole,
    saveRolePermissions,
  } = useRoles();

  const { canDo  } = useAuth();

  // UI State
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [showPermissionModal, setShowPermissionModal] = useState(false);
  const [newRole, setNewRole] = useState("");
  const [selectedRole, setSelectedRole] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedPermissions, setSelectedPermissions] = useState([]); // sidebar permissions
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);

  // Action permission modal state
  const [showActionModal, setShowActionModal] = useState(false);
  const [selectedActionRole, setSelectedActionRole] = useState(null);
  const [selectedActionPermissions, setSelectedActionPermissions] = useState({}); // { Employees: ["view","Add"...], ... }

  // Static sidebar permissions list (you already had this)
  const permissionsList = {
    Dashboard: ["Dashboard"],
    Employees: ["Employees"],
    Departments: ["Departments"],
    Designations: ["Designations"],
    Leaves: ["Leaves"],
    Holidays: ["Holidays"],
    Leave_Types: ["Leave Types"],
    Attendance: ["Attendance"],
    AttendenceReport: ["AttendenceReport"],
    Timesheet: ["Timesheet"],
    "Calendar View": ["Calendar View"],
    Payroll: ["Payroll"],
    Clients: ["Clients"],
    Projects: ["Projects"],
    Project_types: ["Project Types"],
    Reports: ["Reports"],
    Invoice: ["Invoice"],
    Profile: ["User Profile"],
    Settings: ["Settings"],
    Roles: ["Roles"],
    Work: ["Work"],
    EmployeeWork: ["EmployeeWork"],
    Task: ["Task"],
    RequestManagement : ["Request Management"],
    PolicyManagement: ["Policy Management"],
    AnnouncementManagement: ["Announcements"],
    "Asset Categories": ["Asset Categories"], 
    "Asset Types": ["Asset Types"],
    "Assets Management": ["Assets Management"],
    "PolicyCategoryManagement": ["Policy Category"],
    WorkplaceIssues: ["Workplace Issues"],    
    ManagerDashboard: ["Manager Dashboard"],
    BonusTypes: ["Bonus Types"],
    DocumentTypes: ["Document Types"],
    Letters: ["Letters"],
    Certificates: ["Certificates"],
    ProjectInsights: ["Project Insights"],
    OrganizationChart: ["Organization Chart"],
    WorkplaceIssuesCategory: ["Workplace Issues Category"],
    "Resume Management": ["Resume Management"],
    "Audit Logs": ["Audit Logs"],
            
  };

  // Static page actions (match backend casing: Add/Edit/Delete/View or view)
  const pageActions = {
    Dashboard:["view"],
    Employees: ["View", "Add", "Edit", "Delete"],
    Departments: ["View", "Add", "Edit", "Delete"],
    Designations: ["View", "Add", "Edit", "Delete"],
    Leaves: ["View", "Add", "Edit", "Delete"],
    LeaveTypes: ["View", "Add", "Edit", "Delete"],
    Holidays: ["view", "Add", "Edit", "Delete"],
    Attendance: ["View", "Add", "Edit", "Delete"],
    AttendenceReport: ["View", "Add", "Edit", "Delete"],
    Timesheet: ["View", "Add", "Edit", "Delete"],
    Payroll: ["View", "Add", "Edit", "Delete"],
    Clients: ["View", "Add", "Edit", "Delete"],
    Projects: ["View", "Add", "Edit", "Delete"],
    ProjectTypes: ["View", "Add", "Edit", "Delete"],
    Reports: ["View", "Add", "Edit", "Delete"],
    Invoice: ["View", "Add", "Edit", "Delete"],
    UserProfile: ["View", "Add", "Edit", "Delete"],
    Settings: ["View", "Add", "Edit", "Delete"],
    Roles: ["View", "Add", "Edit", "Delete"],
    Work: ["View", "Add", "Edit", "Delete"],
    EmployeeWork: ["View", "Add", "Edit", "Delete"],
    Task: ["View", "Add", "Edit", "Delete"],
    RequestManagement: ["View", "Add", "Edit", "Delete"],
    PolicyManagement: ["View", "Add", "Edit", "Delete"],
    "AnnouncementManagement": ["View", "Add", "Edit", "Delete"],
    "Asset Categories": ["View", "Add", "Edit", "Delete"],
    "Asset Types": ["View", "Add", "Edit", "Delete"],
    "Asset Inventory": ["View", "Add", "Edit", "Delete"],
    "Asset Maintenance": ["View", "Add", "Edit", "Delete"],
    "Asset Return": ["View", "Add", "Edit", "Delete"],

    PolicyCategoryManagement: ["View", "Add", "Edit", "Delete"],
    "Calendarview": ["View", "Add", "Edit", "Delete"],
    WorkplaceIssues: ["View", "Add", "Edit", "Delete"],
    ManagerDashboard: ["View"],
    BonusTypes: ["View", "Add", "Edit", "Delete"],
    DocumentTypes: ["View", "Add", "Edit", "Delete"],
    Letters: ["View", "Add", "Edit", "Delete"],
    Certificates: ["View", "Add", "Edit", "Delete"],
    ProjectInsights: ["View"], 
    OrganizationChart: ["View"],
    WorkplaceIssuesCategory: ["View", "Add", "Edit", "Delete"],
    "Resume Management": ["View", "Add", "Edit", "Delete"], 
    "Audit Logs":["view"],
            
  };

  // Load roles on mount
  useEffect(() => {
    fetchRoles();
  }, []);

  const resetRoleModal = () => {
    setNewRole("");
    setSelectedRole(null);
    setSelectedPermissions([]);
    setIsEditing(false);
    setShowRoleModal(false);
  };

  // SEARCH + PAGINATION
  const filteredRoles = (roles || []).filter((r) =>
    (r.role_name || "").toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalPages = Math.ceil(filteredRoles.length / itemsPerPage) || 1;

const paginatedRoles = filteredRoles.slice(
  (currentPage - 1) * itemsPerPage,
  currentPage * itemsPerPage
);

  // -------------------------------------------------------
  // CREATE or EDIT ROLE
  // -------------------------------------------------------
  const handleSaveRole = async () => {
    if (!newRole.trim()) return;

    const roleData = {
      role_name: newRole,
      status: selectedRole?.status ?? true,
    };

    let success = false;

    if (isEditing) {
      success = await updateRole(selectedRole.id, roleData);
    } else {
      success = await createRole(roleData);
    }

    if (success) {
      resetRoleModal();
    }
  };

  // -------------------------------------------------------
  // DELETE ROLE
  // -------------------------------------------------------
  const handleDeleteRole = async (id) => {
    await deleteRole(id);
  };

  // -------------------------------------------------------
  // STATUS UPDATE
  // -------------------------------------------------------
  const handleToggleStatus = async (role) => {
    const newStatus = role.status === "active" ? "inactive" : "active";
    await updateStatus(role.id, newStatus);
  };

  // -------------------------------------------------------
  // SAVE SIDEBAR PERMISSIONS (Permission Modal)
  // -------------------------------------------------------
  const handleSavePermissions = async () => {
    // selectedPermissions is an array (sidebar)
    const success = await updatePermissions(selectedRole.id, selectedPermissions);
    if (success) {
      setShowPermissionModal(false);
    } else {
      console.log("Failed to update sidebar permissions");
    }
  };
  

  // ------------------------------
  // Helper: determine identifier for roleactionpermission endpoint
  // Prefer role_name (string), otherwise fallback to id
  // ------------------------------
  const getRoleIdentifier = (role) => {
    if (!role) return null;
    if (role.role_name && typeof role.role_name === "string" && role.role_name.trim() !== "") {
      return encodeURIComponent(role.role_name);
    }
    if (role.id || role._id) return role.id ?? role._id;
    return null;
  };

   // ---------------------------
  // OPEN MODAL + LOAD PERMISSIONS
  // ---------------------------
  const openActionModal = async (role) => {
    setSelectedActionRole(role);
    setSelectedActionPermissions({});
    setShowActionModal(true);

    // 1️⃣ Use local role permissions if available
    const raw =
      role.action_permissions ??
      role.actionPermissions ??
      role.actionPermissionsJson ??
      null;

    if (raw) {
      let parsed = raw;

      if (typeof raw === "string") {
        try {
          parsed = JSON.parse(raw);
        } catch {
          parsed = null;
        }
      }

      if (parsed && typeof parsed === "object") {
        setSelectedActionPermissions(parsed);
        return;
      }
    }

    // 2️⃣ Otherwise fetch from backend context API
    const identifier = role.id ?? role.role_name;
    const apiPerms = await getPermissionsByRole(identifier);

    setSelectedActionPermissions(apiPerms);
  };

  // ---------------------------
  // SAVE PERMISSIONS
  // ---------------------------
  const saveActionPermissions = async () => {
    if (!selectedActionRole) return;

    const identifier = selectedActionRole.role_name;  // 🔥 FIXED

    const result = await saveRolePermissions(
      identifier,
      selectedActionPermissions
    );

    if (result.success) {
      setShowActionModal(false);
    }
  };


  // ------------------------------
  // Utility: safely access role.permissions which might be string or array
  // ------------------------------
  const parsePermissionsField = (val) => {
    if (!val) return [];
    if (Array.isArray(val)) return val;
    if (typeof val === "string") {
      try {
        const parsed = JSON.parse(val);
        if (Array.isArray(parsed)) return parsed;
      } catch {
        // fallback: if comma separated
        return val.split(",").map(s => s.trim()).filter(Boolean);
      }
    }
    return [];
  };

  return (
    <div className="bg-sky-50 min-h-screen">
      <div className="px-6 py-6 sm:px-9 max-sm:px-4">
    {/* Header */}
<div className="flex flex-col sm:flex-row justify-between items-center gap-3">
  <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-5">
    Manage Roles
  </h1>
  {/* Desktop button only */}
  {canDo("Roles", "Add") && (
    <button
      className="hidden sm:flex items-center text-white px-4 py-2 rounded-lg "
      onClick={() => {
        setIsEditing(false);
        setNewRole("");
        setSelectedPermissions([]);
        setShowRoleModal(true);
      }}
    style={{ 
  background: "linear-gradient(135deg, #2563eb, #d12465)"
}}>
      <Plus size={18} className="mr-2" /> Create Role
    </button>
  )}
</div>

{/* Search + Mobile Button (white box) */}
<div className="mb-5">
  <div className="bg-white p-3 rounded-lg shadow-sm border flex flex-col gap-3">
    {/* Search row */}
    <div className="flex flex-col sm:flex-row sm:items-center gap-3">
      <div className="flex items-center border border-gray-300 rounded-lg px-3 py-2 flex-1 transition-colors duration-150 focus-within:ring-2 focus-within:ring-[#8c57ff]/40 focus-within:border-[#8c57ff]/40">
        <Search className="text-gray-400 mr-2" size={18} />
        <input
          type="text"
          placeholder="Search roles..."
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setCurrentPage(1);
          }}
          className="w-full outline-none bg-transparent"
        />
      </div>

      {searchTerm && (
        <button
          type="button"
          onClick={() => {
            setSearchTerm("");
            setCurrentPage(1);
          }}
          className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium text-red-600 border border-red-200 bg-red-50 hover:bg-red-100 transition whitespace-nowrap shrink-0 w-full sm:w-auto"
        >
          <X size={14} />
          Clear Filters
        </button>
      )}
    </div>

    {/* Mobile button only */}
    {canDo("Roles", "Add") && (
      <button
        className="flex sm:hidden items-center justify-center w-full bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700"
        onClick={() => {
          setIsEditing(false);
          setNewRole("");
          setSelectedPermissions([]);
          setShowRoleModal(true);
        }}
      style={{ 
  background: "linear-gradient(135deg, #2563eb, #d12465)"
}}>
        <Plus size={18} className="mr-2" /> Create Role
      </button>
    )}
  </div>
</div>

    

      {/* Table Section */}
      <div>
        <div className="bg-white rounded-2xl shadow-sm p-4 sm:p-6">
        <div className="overflow-x-auto">
        <div className="min-w-[700px]">
          <table className="w-full table-fixed border-collapse">
            <thead>
              <tr className="bg-sky-100 text-gray-700">
                <th className="py-3 px-4 font-semibold text-center w-[5%]">ID</th>
                <th className="py-3 px-4 font-semibold text-left w-[20%]">Role</th>
                <th className="py-3 px-4 font-semibold text-left w-[25%]">Permissions</th>
                <th className="py-3 px-4 font-semibold text-center w-[15%]">Actions Permission</th>
                <th className="py-3 px-4 font-semibold text-center w-[15%]">Status</th>
                { (canDo("Roles", "Edit") || canDo("Roles", "Delete")) && (
                  <th className="py-3 px-4 font-semibold text-center w-[15%]">Actions</th>
                )}
              </tr>
            </thead>

            <tbody>
              {paginatedRoles.length > 0 ? (
                paginatedRoles.map((role, index) => (
                  <tr key={role._id ?? role.id ?? index} className="border-t text-sm hover:bg-gray-50">
                    {/* Row number */}
                    <td className="py-3 px-4 text-center">
                      {(currentPage - 1) * itemsPerPage + index + 1}
                    </td>

                    <td className="py-3 px-4 whitespace-nowrap">
                      {role.role_name}
                    </td>

                    <td className="py-3 px-4">
                      <button
                        type="button"
                        className="text-blue-600 underline cursor-pointer hover:text-blue-800"
                        onClick={() => {
                          setSelectedRole(role);
                          const perms = parsePermissionsField(role.permissions ?? role.permissions_json ?? null);
                          setSelectedPermissions(perms);
                          setShowPermissionModal(true);
                        }}
                      >
                        Assign Permissions
                      </button>
                    </td>

                    <td className="py-3 px-4 text-center">
                      <button
                        onClick={() => openActionModal(role)}
                        className="text-purple-600 underline hover:text-purple-800"
                      >
                        Page Actions
                      </button>
                    </td>

                    <td className="py-3 px-4 text-center">
                      <button
                        onClick={() => handleToggleStatus(role)}
                        className={`px-3 py-1 rounded-full text-white text-xs font-semibold ${
                          role.status === "active"
                            ? "bg-green-500 hover:bg-green-600"
                            : "bg-red-500 hover:bg-red-600"
                        }`}
                      >
                        {role.status === "active" ? "Active" : "Inactive"}
                      </button>
                    </td>

                    {(canDo("Roles", "Edit") || canDo("Roles", "Delete")) && (
                    <td className="py-3 px-4 text-center">
                      <div className="flex justify-center gap-2">
                        {/* Edit */}
                        {canDo("Roles", "Edit") && (
                        <button
                          onClick={() => {
                            setIsEditing(true);
                            setSelectedRole(role);
                            setNewRole(role.role_name);
                            setSelectedPermissions(parsePermissionsField(role.permissions));
                            setShowRoleModal(true);
                          }}
                          className="hover:text-blue-600"
                        >
                          <Edit2 size={16} />
                        </button>
                        )}

                        {/* Delete */}
                        {canDo("Roles", "Delete") && (
                        <button
                          onClick={() => handleDeleteRole(role.id ?? role._id)}
                          className="hover:text-red-600"
                        >
                          <Trash2 size={16} />
                        </button>
                        )}
                      </div>
                    </td>
                    )}
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan="6"
                    className="py-6 text-center text-gray-500"
                  >
                    No roles found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        </div>

      {/* Pagination */}
      <div className="flex justify-between items-center mt-4">

  {/* Rows per page */}
  <div className="flex items-center gap-2 text-sm">
    <span>Rows:</span>
    <select
      value={itemsPerPage}
      onChange={(e) => {
        setItemsPerPage(Number(e.target.value));
        setCurrentPage(1);
      }}
      className="border border-gray-300 rounded px-2 py-1 outline-none transition-colors duration-150 focus:ring-2 focus:ring-[#8c57ff]/40 focus:border-[#8c57ff]/40"
    >
      {[5, 10, 15, 20].map((n) => (
        <option key={n} value={n}>
          {n}
        </option>
      ))}
    </select>
  </div>

  {/* Pagination buttons */}
  <div className="flex items-center gap-2">

    {/* First */}
    <button
      onClick={() => setCurrentPage(1)}
      disabled={currentPage === 1}
      className="px-2.5 py-1.5 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
    >
      «
    </button>

    {/* Prev */}
    <button
      onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
      disabled={currentPage === 1}
      className="px-2.5 py-1.5 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
    >
      ‹
    </button>

    {/* Pages (FAST + CLEAN) */}
    {Array.from({ length: totalPages }, (_, i) => i + 1)
      .filter((p) => {
        return (
          p === 1 ||
          p === totalPages ||
          (p >= currentPage - 1 && p <= currentPage + 1)
        );
      })
      .map((page) => (
        <button
          key={page}
          onClick={() => setCurrentPage(page)}
          className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
            currentPage === page
              ? "text-white bg-[linear-gradient(135deg,#2563eb,#d12465)]"
              : "bg-gray-100 text-gray-600 hover:bg-gray-200"
          }`}
        >
          {page}
        </button>
      ))}

    {/* Next */}
    <button
      onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
      disabled={currentPage === totalPages}
      className="px-2.5 py-1.5 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
    >
      ›
    </button>

    {/* Last */}
    <button
      onClick={() => setCurrentPage(totalPages)}
      disabled={currentPage === totalPages}
      className="px-2.5 py-1.5 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
    >
      »
    </button>

  </div>
</div>
        </div>
      </div>

      {/* ---------------------------------------- */}
      {/* ROLE MODAL */}
      {/* ---------------------------------------- */}
      {showRoleModal && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex justify-center items-center z-50 p-4">
          <div className="bg-white w-full max-w-lg rounded-lg p-5">
            <h2 className="text-xl font-semibold mb-4">
              {isEditing ? "Edit Role" : "Create Role"}
            </h2>

            {/* Role Name */}
            <label className="text-sm text-gray-600">Role Name</label>
            <input
              type="text"
              value={newRole}
              onChange={(e) => setNewRole(e.target.value)}
              className={inputClass}
            />

            {/* Status */}
            <label className="text-sm text-gray-600">Status</label>
            <select
              value={selectedRole ? (selectedRole.status ? "active" : "inactive") : "active"}
              onChange={(e) =>
                setSelectedRole((prev) => ({
                  ...prev,
                  status: e.target.value === "active",
                }))
              }
              className={inputClass}
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>

            {/* Buttons */}
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={resetRoleModal}
                className="bg-gray-200 px-4 py-2 rounded-lg"
              >
                Cancel
              </button>

              <button
                onClick={handleSaveRole}
                className="px-4 py-2  text-white rounded-lg"
             style={{ 
  background: "linear-gradient(135deg, #2563eb, #d12465)"
}} >
                {isEditing ? "Save Changes" : "Create"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ---------------------------------------- */}
      {/* PERMISSION MODAL (Sidebar permissions) */}
      {/* ---------------------------------------- */}
      {showPermissionModal && selectedRole && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex justify-center items-center z-50 p-4">
          <div className="bg-white w-full max-w-lg rounded-lg p-5 max-h-[80vh] overflow-y-auto">
            <h2 className="text-xl font-semibold mb-4">
              {selectedRole.role_name} – Sidebar Permissions
            </h2>

            {Object.keys(permissionsList).map((category) => (
              <div key={category} className="mb-4">
                <h3 className="font-medium">{category}</h3>
                <div className="ml-4 space-y-2">
                  {permissionsList[category].map((perm) => (
                    <label key={perm} className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={selectedPermissions.includes(perm)}
                        onChange={(e) =>
                          setSelectedPermissions((prev) =>
                            e.target.checked
                              ? [...prev, perm]
                              : prev.filter((p) => p !== perm)
                          )
                        }
                        className="accent-[#8c57ff]"
                      />
                      {perm}
                    </label>
                  ))}
                </div>
              </div>
            ))}

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setShowPermissionModal(false)}
                className="bg-gray-200 px-4 py-2 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={handleSavePermissions}
                className=" text-white px-4 py-2 rounded-lg"
            style={{ 
  background: "linear-gradient(135deg, #2563eb, #d12465)"
}}  >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ---------------------------------------- */}
      {/* ACTION PERMISSION MODAL */}
      {/* ---------------------------------------- */}
      {showActionModal && selectedActionRole && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex justify-center items-center z-50 p-4">
          <div className="bg-white w-full max-w-xl rounded-lg p-5 max-h-[80vh] overflow-y-auto">
            <h2 className="text-xl font-semibold mb-4">
              {selectedActionRole.role_name} – Page Action Permissions
            </h2>

            {Object.keys(pageActions).map((page) => (
              <div key={page} className="mb-4 border-b pb-3">
                <h3 className="font-medium">{page}</h3>

                <div className="ml-4 grid grid-cols-2 gap-2 mt-2">
                  {pageActions[page].map((action) => (
                    <label key={action} className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={
                          Array.isArray(selectedActionPermissions?.[page]) &&
                          selectedActionPermissions[page].includes(action)
                        }
                        onChange={(e) => {
                          setSelectedActionPermissions((prev) => {
                            const updated = { ...(prev || {}) };
                            if (!Array.isArray(updated[page])) updated[page] = [];

                            if (e.target.checked) {
                              // add if not exists
                              if (!updated[page].includes(action)) {
                                updated[page] = [...updated[page], action];
                              }
                            } else {
                              updated[page] = updated[page].filter((a) => a !== action);
                            }
                            return updated;
                          });
                        }}
                        className="accent-[#8c57ff]"
                      />
                      {action.charAt(0).toUpperCase() + action.slice(1)}
                    </label>
                  ))}
                </div>
              </div>
            ))}

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setShowActionModal(false)}
                className="bg-gray-200 px-4 py-2 rounded-lg"
              >
                Cancel
              </button>

              <button
                onClick={async () => {
                  await saveActionPermissions();
                }}
                className=" text-white px-4 py-2 rounded-lg"
            style={{ 
  background: "linear-gradient(135deg, #2563eb, #d12465)"
}}  >
                Save
              </button>
            </div>

          </div>
        </div>
      )}
      </div>
    </div>
  );
}